import moment from 'moment';

/**
 * Compares given string durations (ex 11:00 in 24 hrs format).
 * Returns
 * 		0: If equal
 * 		1: If first is greater
 *	 -1: If second is greater
 * @param {String} value1
 * @param {String} value2
 */
export const compareDuration = (value1, value2) => {
  const dt1 = moment(`1900-01-01T${value1}`);
  const dt2 = moment(`1900-01-01T${value2}`);
  if (dt1.isSame(dt2)) return 0;
  if (dt1.isAfter(dt2)) return 1;
  return -1;
};

/**
 * Adds up given durations and returns resulting duration
 * @param {String} value1
 * @param {String} value2
 */
export const addDuration = (value1, value2) => {
  const dt1 = moment(`1900-01-01T${value1}:00`);
  const dt2 = moment(`1900-01-01T${value2}:00`);
  const sum = dt1.add(dt2.hour(), 'hours').add(dt2.minute(), 'minutes');
  return sum.format('HH:mm');
};

/**
 * Formats given hour and minutes and returns resuling string
 *
 * @param {Number} hour 4
 * @param {Number} min 5
 * @returns {String} 04:05
 */

export const formatDuration = (hour, min) =>
  `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

/**
 * Formats given hour and minutes and returns resuling string
 *
 * @param {Number} hour 4
 * @param {Number} min 5
 * @returns {String} 04 h 05 min
 */

export const formatDurationWithUnits = (hour, min) => {
  let duration = '';
  if (hour) {
    duration += `${String(hour).padStart(2, '0')} h `;
  }
  if (min) {
    duration += `${String(min).padStart(2, '0')} min`;
  }
  return duration;
};

/**
 * Formats given seconds and returns resulting timestamp string
 * Automatically handles hours if duration is >= 1 hour
 *
 * @param {Number} seconds - Total seconds (e.g., 122.64, 3661.5)
 * @returns {String} Formatted timestamp (e.g., "0:00", "2:02" or "25:00:00")
 */
export const formatSecondsToTimestamp = seconds => {
  // Handle falsy values properly - 0 seconds should return "0:00", but false/null/undefined should return empty string
  if (seconds === null || seconds === undefined || seconds === false) return '';

  // Use moment.duration for accurate duration formatting
  const duration = moment.duration(seconds, 'seconds');

  // If duration is >= 1 hour, show H:mm:ss format, otherwise show m:ss
  if (seconds >= 3600) {
    const hours = Math.floor(duration.asHours());
    const mins = duration.minutes();
    const secs = duration.seconds();
    return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(
      2,
      '0'
    )}`;
  }

  const totalMins = duration.minutes();
  const totalSecs = duration.seconds();
  return `${totalMins}:${String(totalSecs).padStart(2, '0')}`;
};

/**
 * For the current year, return start of month and end of month dates divided by quarters
 * @returns {Array} Array of objects with start and end dates for each month in an array for each quarter
 * @example
 * [
  {
    label: 'Q1',
    months: [
      {
        label: 'Jan',
        start: '2023-01-01T00:00:00.000Z',
        end: '2023-01-31T23:59:59.999Z'
      },
      {
        label: 'Feb',
        start: '2023-02-01T00:00:00.000Z',
        end: '2023-02-29T23:59:59.999Z'
      },
      {
        label: 'Mar',
        start: '2023-03-01T00:00:00.000Z',
        end: '2023-03-31T23:59:59.999Z'
      }
    ]
  }
];
 * */
export const getQuarters = () => {
  const quarters = [];
  const currentYear = moment().year();
  const months = moment.monthsShort();
  const quarterLabels = [
    'Q1 (Jan - Mar)',
    'Q2 (Apr - Jun)',
    'Q3 (Jul - Sep)',
    'Q4 (Oct - Dec)'
  ];
  const quarterMonths = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [9, 10, 11]
  ];

  quarterMonths.forEach((quarter, index) => {
    const quarterObj = {
      label: quarterLabels[index],
      value: index + 1,
      months: [],
      start: moment()
        .utc()
        .year(currentYear)
        .month(quarter[0])
        .startOf('month')
        .toISOString(),
      end: moment()
        .utc()
        .year(currentYear)
        .month(quarter[2])
        .endOf('month')
        .toISOString()
    };
    quarter.forEach(month => {
      const start = moment()
        .utc()
        .year(currentYear)
        .month(month)
        .startOf('month')
        .toISOString();
      const end = moment()
        .utc()
        .year(currentYear)
        .month(month)
        .endOf('month')
        .toISOString();
      quarterObj.months.push({
        label: months[month],
        start,
        end
      });
    });
    quarters.push(quarterObj);
  });
  return quarters;
};

export const calculateCurrentQuarter = (
  customStartMonth = 0,
  fromDate = null
) => {
  const currentDate = fromDate ? new Date(fromDate) : new Date();
  const currentMonth = currentDate.getMonth(); // Get current month (0 = Jan, 11 = Dec)

  // Adjust the month index to the custom year start
  const adjustedMonth = (currentMonth - customStartMonth + 12) % 12;

  // Calculate the current quarter (1-based index)
  return Math.floor(adjustedMonth / 3) + 1;
};

export const getCustomQuarters = (
  customYear = new Date().getFullYear(),
  customStartMonth = 0
) => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  // Precompute month data for the whole year
  const months = Array.from({ length: 12 }, (_, i) => {
    const startYear = i < customStartMonth ? customYear + 1 : customYear;
    return {
      label: monthNames[i],
      start: new Date(Date.UTC(startYear, i, 1)).toISOString(),
      end: new Date(
        Date.UTC(startYear, i, daysInMonth(startYear, i), 23, 59, 59, 999)
      ).toISOString()
    };
  });

  // Create quarters based on precomputed month data
  return Array.from({ length: 4 }, (_, i) => {
    const quarterMonths = [];

    // Use forEach to iterate over 3 months for each quarter
    [0, 1, 2].forEach(j => {
      const monthIndex = (customStartMonth + i * 3 + j) % 12; // Wrap around at 12
      const monthYear =
        customStartMonth + i * 3 + j >= 12 ? customYear + 1 : customYear; // Adjust year if needed
      const month = {
        label: monthNames[monthIndex],
        start: new Date(Date.UTC(monthYear, monthIndex, 1)).toISOString(),
        end: new Date(
          Date.UTC(
            monthYear,
            monthIndex,
            daysInMonth(monthYear, monthIndex),
            23,
            59,
            59,
            999
          )
        ).toISOString()
      };
      quarterMonths.push(month);
    });

    return {
      label: `Q${i + 1} (${quarterMonths[0].label} - ${
        quarterMonths[2].label
      })`,
      value: i + 1,
      months: quarterMonths,
      start: quarterMonths[0].start,
      end: quarterMonths[2].end
    };
  });
};
export const getStartEndOfMonth = (month, customYear = moment.year()) => {
  const start = moment()
    .utc()
    .year(customYear)
    .month(month)
    .startOf('month')
    .toISOString();
  const end = moment()
    .utc()
    .year(customYear)
    .month(month)
    .endOf('month')
    .toISOString();

  return { start, end };
};

export const getQuarterDataFromDate = (dateString, customStartMonth = 0) => {
  // Parse the input date
  const date = moment.utc(dateString);
  const year = date.year();

  // Get all quarters for the specified year
  const quarters = getCustomQuarters(year, customStartMonth);

  // Determine which quarter the date falls into
  return quarters.find(quarter =>
    moment.utc(date).isBetween(quarter.start, quarter.end, null, '[]')
  );
};

export const getStartEndOfQuarter = (
  quarterValue,
  customStartMonth,
  customYear = new Date().getFullYear()
) => {
  const quarters = getCustomQuarters(customYear, customStartMonth);
  const quarterData = quarters.find(quarter => quarter.value === quarterValue);
  return { start: quarterData.start, end: quarterData.end };
};

export const calculateYearStartAndEnd = (
  customYear = moment().year(),
  customStartMonth = 0
) => {
  // Start of the year: 1st day of the custom start month
  const yearStart = moment
    .utc(`${customYear}-${customStartMonth + 1}-01`)
    .startOf('day');

  // End of the year: last day of the month before the custom start month in the next year
  const yearEnd = moment
    .utc(`${customYear + 1}-${customStartMonth + 1}-01`)
    .subtract(1, 'days')
    .endOf('day');

  return {
    start: yearStart.toISOString(),
    end: yearEnd.toISOString()
  };
};

const currentYearString = new Date().getFullYear();

export const MONTH_PERIOD_OPTIONS = [
  {
    label: `January ${currentYearString}`,
    value: 0
  },
  {
    label: `February ${currentYearString}`,
    value: 1
  },
  {
    label: `March ${currentYearString}`,
    value: 2
  },
  {
    label: `April ${currentYearString}`,
    value: 3
  },
  {
    label: `May ${currentYearString}`,
    value: 4
  },
  {
    label: `June ${currentYearString}`,
    value: 5
  },
  {
    label: `July ${currentYearString}`,
    value: 6
  },
  {
    label: `August ${currentYearString}`,
    value: 7
  },
  {
    label: `September ${currentYearString}`,
    value: 8
  },
  {
    label: `October ${currentYearString}`,
    value: 9
  },
  {
    label: `November ${currentYearString}`,
    value: 10
  },
  {
    label: `December ${currentYearString}`,
    value: 11
  }
];

/**
 * Returns dates like "3 OCT", "17 NOV" for current year dates, and dates like "3 OCT 22'", "17 NOV 19'" past year dates.
 *
 * @param {string} date - The input date string.
 * @returns {string} The prettified date string.
 */
export const getPrettifiedDate = (date, config = {}, utc = false) => {
  const mm = utc ? moment.utc : moment;
  const now = mm();
  const inputDate = mm(date);
  const { shortFormat = 'D MMM', longFormat = "D MMM 'YY" } = config;

  if (now.year() === inputDate.year()) {
    return inputDate.format(shortFormat);
  }

  return inputDate.format(longFormat);
};

/**
 * To get dates in format like "Jul 26, 2024"
 */

export const getShortMonthDate = dateStr => {
  const date = new Date(dateStr);

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * To get timestamp from the date string in "2:07 PM" format
 */

export const getTimestamp = dateStr => {
  const date = new Date(dateStr);

  return date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
    .replace(' ', '');
};

const monthFullNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export function getMonthName(month) {
  return monthFullNames[month];
}

/**
 * Converts a UTC date string to the user's timezone
 * @param {string|undefined} utcDate - UTC date string (e.g., "2024-01-15T10:30:00Z")
 * @param {string|undefined} userTimezone - User's timezone (e.g., "America/New_York")
 * @returns {moment.Moment} Moment object in user's timezone
 */
export const convertUtcToUserTimezone = (utcDate, userTimezone) => {
  if (!utcDate) return null;

  // Parse the UTC date
  const utcMoment = moment.utc(utcDate);

  // If user timezone is provided, convert to that timezone
  if (userTimezone) {
    // Use moment-timezone if available, otherwise fall back to local conversion
    if (utcMoment.tz) {
      return utcMoment.tz(userTimezone);
    }
    // Fallback: convert to local timezone
    return utcMoment.local();
  }

  // If no user timezone provided, convert to browser's local timezone
  return utcMoment.local();
};

/**
 * Safely converts a date string to ISO format, handling timezone issues and invalid dates
 * @param {string} dateString - Date string in various formats (YYYY-MM-DD, ISO string, etc.)
 * @param {string} userTimezone - User's timezone (e.g., "America/New_York") for proper date handling
 * @returns {string} ISO string or original string if conversion fails
 */
export const safeToISOString = (dateString, userTimezone = null) => {
  if (!dateString) return dateString;

  try {
    let momentDate;

    if (userTimezone) {
      // If user timezone is provided, parse in that timezone
      // Check if moment-timezone is available
      if (moment.tz) {
        momentDate = moment.tz(dateString, userTimezone);
      } else {
        // Fallback to local parsing if moment-timezone not available
        momentDate = moment(dateString);
      }
    } else {
      // Fallback to local parsing
      momentDate = moment(dateString);
    }

    // Check if the date is valid
    if (!momentDate.isValid()) {
      return dateString;
    }

    return momentDate.toISOString();
  } catch (error) {
    // Fallback to original string if date parsing fails
    return dateString;
  }
};
